<template>
    <div>
        <NavbarOne/>

        <div class="flex">
            <div class="w-1/2 hidden md:block lg:flex-1">
                <img class="h-full object-cover" :src="bg" alt="reset password">
            </div>
            <div class="w-full md:w-1/2 lg:max-w-lg xl:max-w-3xl lg:w-full py-16 px-[20px] sm:px-8 lg:p-16 xl:p-24 relative z-10 flex items-center overflow-hidden">
                <div class="mx-auto md:mx-0 max-w-md w-full">
                    <h2 class="leading-none" data-aos="fade-up">Choose a new password</h2>
                    <p v-if="email" class="text-lg mt-[15px]" data-aos="fade-up" data-aos-delay="100">
                        for <span class="text-primary">{{ email }}</span>
                    </p>

                    <p v-if="!token || !email" class="mt-7 text-base text-[#E13939]">
                        This reset link is incomplete. Please open the link from your email again, or
                        <router-link to="/forger-password" class="text-primary">request a new one</router-link>.
                    </p>

                    <form v-else class="mt-7" @submit.prevent="submit">
                        <div data-aos="fade-up" data-aos-delay="200">
                            <label for="new-password" class="text-base sm:text-lg font-medium leading-none mb-2.5 block dark:text-white">New password</label>
                            <input
                                id="new-password"
                                v-model="password"
                                class="w-full h-12 md:h-14 bg-white dark:bg-transparent border border-bdr-clr focus:border-primary p-4 outline-none duration-300"
                                type="password"
                                autocomplete="new-password"
                                required
                            >
                            <p v-if="fieldErrors.password" class="mt-2 text-sm text-[#E13939]">{{ fieldErrors.password }}</p>
                        </div>
                        <div class="mt-5" data-aos="fade-up" data-aos-delay="250">
                            <label for="confirm-password" class="text-base sm:text-lg font-medium leading-none mb-2.5 block dark:text-white">Confirm new password</label>
                            <input
                                id="confirm-password"
                                v-model="passwordConfirmation"
                                class="w-full h-12 md:h-14 bg-white dark:bg-transparent border border-bdr-clr focus:border-primary p-4 outline-none duration-300"
                                type="password"
                                autocomplete="new-password"
                                required
                            >
                        </div>
                        <div data-aos="fade-up" data-aos-delay="300">
                            <button type="submit" class="btn btn-theme-solid mt-[15px]" :data-text="submitting ? 'Saving...' : 'Save password'" :disabled="submitting">
                                <span>{{ submitting ? 'Saving...' : 'Save password' }}</span>
                            </button>
                        </div>
                    </form>

                    <p v-if="successMessage" class="text-base mt-5 text-[#31A051]">{{ successMessage }}</p>
                    <p v-if="generalError" class="text-base mt-5 text-[#E13939]">{{ generalError }}</p>
                </div>
            </div>
        </div>
        <FooterOne/>

        <ScrollToTop/>

    </div>
</template>

<script setup>
    import { onMounted, ref } from 'vue';
    import { useRoute, useRouter } from 'vue-router';

    import NavbarOne from '@/components/navbar/navbar-one.vue';
    import FooterOne from '@/components/footer/footer-one.vue';
    import ScrollToTop from '@/components/scroll-to-top.vue';

    import bg from '@/assets/img/bg/forget-pass.jpg'
    import Aos from 'aos';
    import { apiRequest } from '@/lib/client-api';

    onMounted(() => {
        Aos.init();
    });

    const route = useRoute();
    const router = useRouter();

    // Both arrive on the link we emailed.
    const token = ref(route.query.token || '');
    const email = ref(route.query.email || '');

    const password = ref('');
    const passwordConfirmation = ref('');
    const submitting = ref(false);
    const successMessage = ref('');
    const generalError = ref('');
    const fieldErrors = ref({});

    async function submit() {
        submitting.value = true;
        successMessage.value = '';
        generalError.value = '';
        fieldErrors.value = {};

        try {
            const response = await apiRequest('/auth/reset-password', {
                method: 'POST',
                data: {
                    token: token.value,
                    email: email.value,
                    password: password.value,
                    password_confirmation: passwordConfirmation.value,
                },
            });

            successMessage.value = `${response.message} Taking you to the login page...`;
            setTimeout(() => router.push('/login'), 2000);
        } catch (error) {
            fieldErrors.value = {
                password: error.errors?.password?.[0] || '',
            };
            generalError.value = error.errors?.email?.[0]
                || (fieldErrors.value.password ? '' : (error.message || 'We could not reset your password.'));
        } finally {
            submitting.value = false;
        }
    }
</script>
